import { getRegion } from 'utils/Region';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { postHboModalDismiss } from 'services/api/hbo/modalDismiss';
import { useHboActivationStatus } from './useHboActivationStatus';
import { useHboActivate } from './useHboActivate';

const HOME_PATHNAME_PATTERN = /\/homeuser(\/|$|\?)/;

// Punto unico de orquestacion consumido por NodesLayout y player.provider (misma logica, evita duplicarla)
export const useHboActivationController = pathname => {
  const { activeComponent, status, showActivationModal, loading, refetch } = useHboActivationStatus();
  const { activate, hasError, clearError, errorCode } = useHboActivate();
  const wasOnHomeUserRef = useRef(
    HOME_PATHNAME_PATTERN.test(pathname || '') && !pathname.includes('/vcard'),
  );
  const [deferred, setDeferred] = useState(false);
  // El backend puede seguir devolviendo status=pending durante toda la sesion; sin este flag
  // "Aceptar"/"Cerrar"/"No volver a mostrar" nunca lograrian ocultar el modal (ver AC 7.3.3/7.4.2/7.4.3)
  const [promotionDismissed, setPromotionDismissed] = useState(false);
  // Flag para indicar que el modal fue activado por requestActivation (banner, botón Premium, etc)
  // Esto resuelve el deadlock: el modal necesita hasRecognizedEntryPoint pero la navegación no ocurre hasta que el modal se cierre
  const [comesFromRequestActivation, setComesFromRequestActivation] = useState(false);
  // CRÍTICO: Flag que suprime el modal de activación después de cerrar un error
  // Esto previene que el modal de activación reaparezca después de que se cierre el modal de error
  const [suppressInitialModalAfterError, setSuppressInitialModalAfterError] = useState(false);
  // Evita que el modal inicial reaparezca al navegar despues de haberlo cerrado (Activar
  // ahora / Lo hare despues / icono X); solo se reactiva con un nuevo requestActivation
  const [initialModalDismissed, setInitialModalDismissed] = useState(false);

  const isOnHomeUser = HOME_PATHNAME_PATTERN.test(pathname || '') && !pathname.includes('/vcard');
  
  const showInitialModal =
    activeComponent === 'initial' && !suppressInitialModalAfterError && !initialModalDismissed && !isOnHomeUser;
  const showPromotionModal = !promotionDismissed && deferred;

  // Limpiar flag de supresión cuando el activeComponent cambie
  // Esto permite que el modal de activación reaparezca en futuras interacciones
  useEffect(() => {
    setSuppressInitialModalAfterError(false);
  }, [activeComponent]);

  // WEBCL-3850: volver a Home no debe reconsultar /status. Solo limpiamos flags
  // efimeros cuando realmente se entra a homeuser desde otra ruta.
  useEffect(() => {
    const isActuallyOnHomeUser = HOME_PATHNAME_PATTERN.test(pathname || '') && !pathname.includes('/vcard');
    const enteredHomeUser = isActuallyOnHomeUser && !wasOnHomeUserRef.current;

    if (enteredHomeUser && pathname) {
      // Limpiar flag de requestActivation en homeuser para evitar interferencias
      // en futuras navegaciones a contenido HBO
      if (comesFromRequestActivation) {
        setComesFromRequestActivation(false);
      }
    }

    wasOnHomeUserRef.current = isActuallyOnHomeUser;
  }, [pathname, comesFromRequestActivation]);


  // Intercepta un punto de entrada (ej. click en el submenu Premium): 
  // CRÍTICO: Ejecutar navegación INMEDIATAMENTE (sin mostrar modal antes)
  // El modal se mostrará DESPUÉS de llegar al destino (vcard o pandg_hbov2)
  // Esto permite que la redirección ocurra primero, y el modal se muestre en el destino correcto
  const requestActivation = useCallback(
    onProceed => {
      // Ejecutar navegación de inmediato, sin diferir
      onProceed();
      // Marcar que viene de requestActivation para que el modal lo sepa
      // El modal se mostrará cuando se complete la navegación y lleguemos al destino
      setComesFromRequestActivation(true);
      // Limpiar promotionDismissed para permitir que el modal se muestre de nuevo
      if (promotionDismissed) {
        setPromotionDismissed(false);
      }
      // Nueva interaccion del usuario: permite que el modal inicial vuelva a mostrarse
      if (initialModalDismissed) {
        setInitialModalDismissed(false);
      }
      if (suppressInitialModalAfterError) {
        setSuppressInitialModalAfterError(false);
      }
      // WEBCL-3778: cada accionable de contenido HBO dispara una nueva consulta de
      // estado (refetch corta la llamada por si mismo si la cuenta ya se confirmo 'linked')
      refetch();
    },
    [promotionDismissed, initialModalDismissed, suppressInitialModalAfterError, refetch],
  );

  const onDefer = useCallback(() => {
    setDeferred(true);
    setPromotionDismissed(false);
  }, []);

  // Se dispara al cerrar el modal inicial
  // CRÍTICO: La navegación ya ocurrió en requestActivation, no ejecutar navegación aquí
  const onCloseInitialModal = useCallback(() => {
    // Evita que el modal reaparezca en la proxima navegacion (bug: se volvia a mostrar
    // al navegar a otra seccion tras Activar ahora/Lo hare despues)
    setInitialModalDismissed(true);
  }, []);

  // Se dispara al cerrar el modal inicial con el icono "X"
  const onCancelInitialModal = useCallback(() => {
    // El usuario cerró sin elegir opción; tampoco debe reaparecer en la proxima navegacion
    setInitialModalDismissed(true);
  }, []);

  const onClosePromotion = useCallback(() => {
    setDeferred(false);
    // Marcar como dismissed temporalmente para evitar loop infinito en el MISMO contexto
    // Cuando el usuario hace click en banner/botón de nuevo, requestActivation limpiará este flag
    setPromotionDismissed(true);
    // Limpiar flag de requestActivation
    setComesFromRequestActivation(false);
  }, []);

  const onDismissPermanently = useCallback(() => {
    postHboModalDismiss({ suppress_permanently: true }, {region: getRegion()});
    setDeferred(false);
    setPromotionDismissed(true);
    refetch();
  }, [refetch]);

  const onPrimaryAction = useCallback(async (metricValues, activationParams) => {
    const activated = await activate(metricValues, activationParams);
    if (activated) refetch();
  }, [activate, refetch]);

  const activateWithMetrics = useCallback(async (metricValues, activationParams) => {
    const activated = await activate(metricValues, activationParams);
    if (activated) refetch();

    return activated;
  }, [activate, refetch]);

  const onCloseError = useCallback(() => {
    // CRÍTICO: Al cerrar el error, establecer flag que suprime el modal de activación
    // Esto previene que el modal de activación reaparezca después de cerrar el modal de error
    setSuppressInitialModalAfterError(true);
    clearError();

    // Limpiar flag de requestActivation para que no intente mostrar el modal nuevamente
    setComesFromRequestActivation(false);
  }, [clearError]);

  return useMemo(
    () => ({
      status,
      loading,
      showActivationModal,
      showInitialModal,
      showPromotionModal,
      showErrorModal: hasError,
      errorCode,
      forceShowPromotion: deferred,
      requestActivation,
      activateWithMetrics,
      comesFromRequestActivation,
      onDefer,
      onCloseInitialModal,
      onCancelInitialModal,
      onClosePromotion,
      onDismissPermanently,
      onPrimaryAction,
      onCloseError,
      resetPromotionDismissed: () => setPromotionDismissed(false),
    }),
    [
      status,
      loading,
      showActivationModal,
      showInitialModal,
      showPromotionModal,
      hasError,
      errorCode,
      deferred,
      promotionDismissed,
      comesFromRequestActivation,
      requestActivation,
      activateWithMetrics,
      onDefer,
      onCloseInitialModal,
      onCancelInitialModal,
      onClosePromotion,
      onDismissPermanently,
      onPrimaryAction,
      onCloseError,
    ],
  );
};